import { getBackupDomains, saveBackupDomains } from "./db";
import { probeRequestTest } from "./probe";

let gateResolved = false;
let resolveApiGate!: () => void;

export const apiGate = new Promise<void>(resolve => {
  resolveApiGate = resolve;
});

function releaseApiGate() {
  if (gateResolved) return;
  gateResolved = true;
  resolveApiGate();
}

// ====================================================================
// 代理状态（同步可读）：供页面组件决定是否走 SW 反代专用链路
// ====================================================================

let proxyActive = false;
type ProxyStateListener = (active: boolean) => void;
const proxyStateListeners = new Set<ProxyStateListener>();

// 运行时翻转「软恢复」事件名：页面使用中主域名失效、代理刚切到备用域名时派发一次，
// 让卡在加载态的首页数据就地重拉。**不整页 reload** —— 整页 reload 会打断正在进行的游戏
// （游戏在第三方 CDN 的 iframe 里），明确不可接受。订阅方各自重拉自己的数据源：
//   - GlobalDataContext（游戏列表/分类等 useGlobalRemoteList）→ clearData() bump changeTime
//   - homeDisplay store → loadConfig()（banner / 活动 / 弹窗配置）
//   - user store → 重新拉用户/访客信息
// 失败请求当时未写缓存，重拉会真正发起网络请求并经 SW 代理走备用域名成功。
export const PROXY_ACTIVATED_EVENT = "proxy:activated";

// 「软恢复」武装开关。初始同步阶段（启动时 SW 已处于代理、bootstrap 乐观置位）的 false→true
// 不是"运行时翻转"：那时页面刚加载、数据本就经代理拉取，无需也不应再触发一轮重拉（会和首屏
// 加载抢跑/重复请求）。bootstrap 状态同步完成后才武装；之后页面使用中的 false→true 才软恢复。
let liveFlipArmed = false;
// 软恢复派发合流窗口：两次 proxy:activated 至少间隔这么久才再派发。
// SW 侧本就把"恢复→再失效"循环限流在 ≥5s（maybeRecoverPrimary 指数退避 5s 起 + confirmPrimaryDead
// 单飞/2.5s TTL/两次硬错才判死），正常永远撞不到这条；它只是防御性兜底，确保即便上游抖动加剧
// （或日后退避被调小）也不会把首页接口刷成风暴。取值 < SW 5s 退避，故绝不会误挡真实的恢复跃迁。
const PROXY_ACTIVATED_MIN_INTERVAL_MS = 3000;
let lastProxyActivatedAt = 0;

function setProxyActive(next: boolean): void {
  if (proxyActive === next) return;
  proxyActive = next;
  proxyStateListeners.forEach(fn => {
    try {
      fn(next);
    } catch {
      // listener 异常不影响其它订阅
    }
  });
  // 代理切到备用域名瞬间：通知已裂的图片重载（LazyImage / OptimizedImage 都监听该事件，
  // 仅重载 imageError/retryCount>0 的失败图片）。此时 SW 会把图片反代到备用域名，能真正取回。
  // 与「数据软恢复」解耦：图片在 boot-in-proxy 早期 race 裂图也能自愈，故每次 true 都派发。
  if (next && typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("image-reload-trigger"));
    } catch {
      // 派发失败不影响代理状态本身
    }
  }
}

// 运行时代理翻转入口：页面使用中主域名失效、SW 广播过来时调用。false→true 且已武装时，
// 派发一次软恢复事件让卡住的首页数据就地重拉（不重载、不碰游戏 iframe），修复"不刷新则首页
// 一直加载中"。其余转换（recovery 的 true→false、备用间切换的 true→true）不触发。
function handleRuntimeProxyFlip(next: boolean): void {
  const wasActive = proxyActive;
  setProxyActive(next);
  if (!(liveFlipArmed && !wasActive && next && typeof window !== "undefined")) {
    return;
  }
  // 合流：抖动场景下若上一次软恢复派发还在窗口内，跳过本次，避免反复刷首页接口。
  const now = Date.now();
  if (now - lastProxyActivatedAt < PROXY_ACTIVATED_MIN_INTERVAL_MS) return;
  lastProxyActivatedAt = now;
  try {
    window.dispatchEvent(new CustomEvent(PROXY_ACTIVATED_EVENT));
  } catch {
    // 派发失败不影响代理状态本身（订阅方收不到则维持原状，用户仍可手动刷新兜底）
  }
}

// 在「初始状态同步」(refreshProxyStateFromSw 的 QUERY_PROXY_STATE 往返)完成后才武装。
// 这样武装前 proxyActive 必已与 SW 当前态对齐：boot-in-proxy 会被静默置 true、bootstrap 探活
// 硬错的乐观置位也在此前同步完成 —— 此后任何 PROXY_STATE_CHANGED{true} 广播到达时 wasActive
// 已是 true，handleRuntimeProxyFlip 不会把启动遗留代理态误判为 false→true 翻转去触发软恢复。
// 不挂在 releaseApiGate：apiGate 释放可能早于 QUERY 往返 resolve，会留出"已武装但状态尚未
// 同步"的竞态窗口（评审 2026-06-19 阻断项）。
function armFailoverRecovery(): void {
  liveFlipArmed = true;
}

export function isProxyActive(): boolean {
  return proxyActive;
}

export function subscribeProxyState(listener: ProxyStateListener): () => void {
  proxyStateListeners.add(listener);
  return () => {
    proxyStateListeners.delete(listener);
  };
}

// 监听 SW 推回的 PROXY_STATE_CHANGED 广播
if (typeof navigator !== "undefined" && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener("message", evt => {
    const data = evt.data as { type?: string; proxyMode?: boolean } | null;
    if (data && data.type === "PROXY_STATE_CHANGED") {
      // 运行时翻转：false→true（已武装）派发软恢复事件，让首页数据就地以备用域名重拉（不重载）。
      handleRuntimeProxyFlip(!!data.proxyMode);
    }
  });
}

// 启动时主动拉一次状态（SW 可能因刷新/更新已经处于代理模式）
async function refreshProxyStateFromSw(): Promise<void> {
  try {
    const res = (await sendToSw({ type: "QUERY_PROXY_STATE" })) as
      | { proxyMode?: boolean }
      | { error?: string }
      | null;
    if (res && "proxyMode" in res) {
      setProxyActive(!!res.proxyMode);
    }
  } catch {
    // 查询失败保持默认 false
  }
}

function normalizeDomains(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  return Array.from(
    new Set(
      list
        .map(item =>
          String(item || "")
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
  );
}

function getRuntimeBackupDomains(): string[] {
  return normalizeDomains(window._config?.backupDomains);
}

async function getFallbackList(): Promise<string[]> {
  const runtime = getRuntimeBackupDomains();
  if (runtime.length > 0) return runtime;
  return getBackupDomains();
}

// 主动申请持久化配额，降低 IndexedDB / Cache Storage 在长期失败站点上被淘汰的概率
async function requestPersistentStorage(): Promise<void> {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.storage &&
      typeof navigator.storage.persist === "function"
    ) {
      await navigator.storage.persist();
    }
  } catch {
    // 申请失败不影响主流程
  }
}

// 设备是否"明确离线"：与 SW public/sw.js 的 isDefinitelyOffline 同口径。
// WebView 里 navigator.onLine 常恒为 true、不可信，故再看 NetworkInformation.type==='none'
// 这个"无任何网络接口"的确定性信号；type 不支持时退回 onLine。仅在显式离线时为 true。
interface NetworkInformationLike {
  type?: string;
}
function isDeviceOffline(): boolean {
  if (typeof navigator === "undefined") return false;
  if (navigator.onLine === false) return true;
  const nav = navigator as Navigator & { connection?: NetworkInformationLike };
  return nav.connection?.type === "none";
}

// 通知 SW 进入代理模式（辅助手段：SW 自身在 fetch 失败时也会自动进入代理）
function notifySwEnterProxyMode(list: string[]): void {
  try {
    const controller = navigator.serviceWorker?.controller;
    if (controller) {
      controller.postMessage({ type: "ENTER_PROXY_MODE", backupDomains: list });
    }
  } catch {
    // SW 不可用时静默失败
  }
}

// 调试工具：在 DevTools Console 中调用
// __forceProxy('www.djdltest.com')  — 强制 SW 代理到指定备用域名
// __exitProxy()                     — 退出代理模式，恢复直连主域名
// __proxyState()                    — 查看当前代理状态
function sendToSw(msg: Record<string, unknown>): Promise<unknown> {
  return new Promise(resolve => {
    const controller = navigator.serviceWorker?.controller;
    if (!controller) {
      resolve({ error: "no SW controller" });
      return;
    }
    const channel = new MessageChannel();
    channel.port1.onmessage = e => resolve(e.data);
    setTimeout(() => resolve({ error: "timeout" }), 2000);
    controller.postMessage(msg, [channel.port2]);
  });
}

if (typeof window !== "undefined") {
  (window as any).__forceProxy = async (backupHost?: string) => {
    const host = backupHost || window._config?.backupDomains?.[0];
    if (!host) {
      console.error("请传入备用域名，如 __forceProxy('www.djdltest.com')");
      return;
    }
    const target = host.startsWith("http") ? host : `https://${host}`;
    const res = await sendToSw({ type: "FORCE_PROXY_MODE", target });
    console.log("[DomainGuard] 已强制进入代理模式 →", target, res);
    return res;
  };
  (window as any).__exitProxy = async () => {
    const res = await sendToSw({ type: "EXIT_PROXY_MODE" });
    console.log("[DomainGuard] 已退出代理模式，恢复直连", res);
    return res;
  };
  (window as any).__proxyState = async () => {
    const res = await sendToSw({ type: "QUERY_PROXY_STATE" });
    console.log("[DomainGuard] 当前状态:", res);
    return res;
  };
  // 模拟"禁用主域名"——故障注入，让真实容灾判定运行（区别于 __forceProxy 的直接强制进代理）。
  // mode: 'network'(默认) | 'server'(可带 status) | 'timeout' | 'offline'
  (window as any).__simulatePrimaryDown = async (
    mode: "network" | "server" | "timeout" | "offline" = "network",
    status?: number
  ) => {
    const res = await sendToSw({ type: "SW_DEBUG_FAIL_PRIMARY", mode, status });
    console.log(
      `[DomainGuard] 已模拟主域名故障 mode=${mode}${status ? " status=" + status : ""}；` +
        `刷新或发起请求即可观察容灾行为，__simulatePrimaryUp() 恢复。` +
        `（注意：network 模式需已注入 backupDomains 且备用探活可达才会真正切换；无备用域名时按设计不切。）`,
      res
    );
    return res;
  };
  // 模拟"恢复主域名"——清除故障注入并立即尝试切回主域名。
  (window as any).__simulatePrimaryUp = async () => {
    const res = await sendToSw({ type: "SW_DEBUG_RESTORE_PRIMARY" });
    console.log(
      "[DomainGuard] 已恢复主域名（清除故障注入 + 尝试切回主域名）",
      res
    );
    return res;
  };
}

export async function bootstrapDomainGuard(): Promise<void> {
  // 全场景启用：普通 Web / PWA / InAppWebView 均执行容灾流程
  // 唯一前置：当前环境支持 Service Worker（否则容灾依赖的 SW 反代不可用，
  // 此时仍执行探活、但探活失败也无法切域 —— 等同于"尽力而为"）
  // if (!utils.updateCheckIsPwa()) {
  //   releaseApiGate();
  //   return;
  // }

  // PWA 场景下尽早申请持久化存储配额；不阻塞探活
  void requestPersistentStorage();

  // SW 可能已经因为上次刷新/更新而处于代理模式，主动拉一次状态同步给主线程。
  // 同步完成后才武装"运行时翻转软恢复"：确保武装前 proxyActive 已与 SW 当前态对齐，
  // 避免启动遗留代理态被后续广播误判为 false→true 而误触发一轮重拉（见 armFailoverRecovery 注释）。
  // sendToSw 自带 2s 超时且无 controller 时立即 resolve，arm 不会被无限期推迟。
  void refreshProxyStateFromSw().finally(armFailoverRecovery);

  const probe = await probeRequestTest();
  if (probe.ok) {
    const domains = getRuntimeBackupDomains();
    if (domains.length > 0) {
      await saveBackupDomains(domains, location.origin);
    }
    releaseApiGate();
    return;
  }

  // 探活失败 → 确保备用域名列表写入存储（供 SW fetch fallback 读取）
  const list = await getFallbackList();
  if (list.length > 0) {
    await saveBackupDomains(list, location.origin);
    // 仅"硬错(network_error)"且设备在线时才主动通知 SW 进代理 + 乐观置 true：与 SW v9.2 判据
    // 一致——timeout 多是弱网"活着但慢"，切备用域走同一拥塞链路无益还抖动；设备断网切纯空转。
    // 这两类不在启动期抢跑切域。http_error/bad_body 也不抢跑：留给 SW 的 5xx 校验自处。
    // 真·硬失败时 SW fetch-fallback 仍会自行进代理（已写入的 list 供其读取）。
    if (probe.reason === "network_error" && !isDeviceOffline()) {
      notifySwEnterProxyMode(list);
      // 主线程也乐观置 true，避免在 SW 广播到达之前 GameDetail 走错链路
      setProxyActive(true);
    }
  }

  // 立即释放 apiGate：业务请求发出后，SW 的 fetchWithFallback 会在 fetch 失败时
  // 自动进入代理模式并重试，不再依赖 postMessage 的时序
  releaseApiGate();
}
